import React, { useEffect, useState } from "react";
import { api } from "../api/api";

// Secret fields are never sent back by GET (see app.py's masking) -- only
// a "<field>_configured" boolean comes back. Leaving the input blank on
// save means "keep whatever's already stored" (backend logic in
// notify_store.save_notification_config), so the placeholder just tells
// the user that's the case rather than showing a fake value.
const SECRET_FIELDS = {
  email: ["smtp_password"],
  telegram: ["bot_token"],
  sms: ["twilio_auth_token"],
};

const EMPTY = {
  email: { enabled: false, smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", from_email: "", to_email: "" },
  telegram: { enabled: false, bot_token: "", chat_id: "" },
  sms: { enabled: false, twilio_account_sid: "", twilio_auth_token: "", twilio_from: "", to_phone: "" },
  events: { entry: true, exit: true },
};

export default function NotificationSettings() {
  const [cfg, setCfg] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [testResults, setTestResults] = useState(null);

  const refresh = () =>
    api.getNotificationConfig().then((data) => {
      // merge over EMPTY so a fresh/never-saved config doesn't come back
      // missing keys this form expects
      setCfg({
        email: { ...EMPTY.email, ...data.email },
        telegram: { ...EMPTY.telegram, ...data.telegram },
        sms: { ...EMPTY.sms, ...data.sms },
        events: { ...EMPTY.events, ...data.events },
      });
    }).catch(() => {});

  useEffect(() => { refresh(); }, []);

  const setField = (section, field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCfg((c) => ({ ...c, [section]: { ...c[section], [field]: value } }));
  };

  const save = async () => {
    setBusy(true); setStatus(null); setTestResults(null);
    try {
      const data = await api.saveNotificationConfig(cfg);
      setCfg({
        email: { ...EMPTY.email, ...data.email },
        telegram: { ...EMPTY.telegram, ...data.telegram },
        sms: { ...EMPTY.sms, ...data.sms },
        events: { ...EMPTY.events, ...data.events },
      });
      setStatus({ ok: true, msg: "Alert settings saved." });
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTestBusy(true); setTestResults(null); setStatus(null);
    try {
      const results = await api.testNotification();
      setTestResults(results);
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setTestBusy(false);
    }
  };

  const secretPlaceholder = (section, field) =>
    cfg[section][`${field}_configured`] ? "(saved -- leave blank to keep)" : "";

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Alerts</div>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 16 }}>
        Get notified the moment a strategy enters or exits a position. Each channel is optional
        and independent -- enable as many as you like. Nothing here is encrypted at rest, so treat
        these credentials (SMTP password, Telegram bot token, Twilio auth token) the same as any
        other saved secret on this server.
      </div>

      {status && <div style={{ ...styles.banner, ...(status.ok ? styles.bannerOk : styles.bannerErr) }}>{status.msg}</div>}

      <Section title="Email">
        <Row label="Enable">
          <input type="checkbox" checked={cfg.email.enabled} onChange={setField("email", "enabled")} />
        </Row>
        <Row label="SMTP Host"><input style={styles.input} value={cfg.email.smtp_host} onChange={setField("email", "smtp_host")} placeholder="smtp.gmail.com" /></Row>
        <Row label="SMTP Port"><input style={styles.input} value={cfg.email.smtp_port} onChange={setField("email", "smtp_port")} placeholder="587" /></Row>
        <Row label="SMTP User"><input style={styles.input} value={cfg.email.smtp_user} onChange={setField("email", "smtp_user")} placeholder="you@gmail.com" /></Row>
        <Row label="SMTP Password">
          <input style={styles.input} type="password" value={cfg.email.smtp_password} onChange={setField("email", "smtp_password")} placeholder={secretPlaceholder("email", "smtp_password")} />
        </Row>
        <Row label="From Email"><input style={styles.input} value={cfg.email.from_email} onChange={setField("email", "from_email")} placeholder="defaults to SMTP User" /></Row>
        <Row label="To Email"><input style={styles.input} value={cfg.email.to_email} onChange={setField("email", "to_email")} placeholder="where alerts get sent" /></Row>
        <div style={styles.hint}>
          For Gmail, use an App Password (not your normal password) -- Google Account &gt; Security &gt; 2-Step Verification &gt; App Passwords.
        </div>
      </Section>

      <Section title="Telegram">
        <Row label="Enable">
          <input type="checkbox" checked={cfg.telegram.enabled} onChange={setField("telegram", "enabled")} />
        </Row>
        <Row label="Bot Token">
          <input style={styles.input} type="password" value={cfg.telegram.bot_token} onChange={setField("telegram", "bot_token")} placeholder={secretPlaceholder("telegram", "bot_token")} />
        </Row>
        <Row label="Chat ID"><input style={styles.input} value={cfg.telegram.chat_id} onChange={setField("telegram", "chat_id")} placeholder="e.g. 123456789" /></Row>
        <div style={styles.hint}>
          Message @BotFather on Telegram to create a bot and get its token, then message your new bot once and check
          https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates to find your chat_id.
        </div>
      </Section>

      <Section title="SMS (Twilio)">
        <Row label="Enable">
          <input type="checkbox" checked={cfg.sms.enabled} onChange={setField("sms", "enabled")} />
        </Row>
        <Row label="Account SID"><input style={styles.input} value={cfg.sms.twilio_account_sid} onChange={setField("sms", "twilio_account_sid")} /></Row>
        <Row label="Auth Token">
          <input style={styles.input} type="password" value={cfg.sms.twilio_auth_token} onChange={setField("sms", "twilio_auth_token")} placeholder={secretPlaceholder("sms", "twilio_auth_token")} />
        </Row>
        <Row label="Twilio From #"><input style={styles.input} value={cfg.sms.twilio_from} onChange={setField("sms", "twilio_from")} placeholder="+1XXXXXXXXXX" /></Row>
        <Row label="Your Phone #"><input style={styles.input} value={cfg.sms.to_phone} onChange={setField("sms", "to_phone")} placeholder="+91XXXXXXXXXX" /></Row>
        <div style={styles.hint}>
          Requires a paid Twilio account (twilio.com) -- free trial numbers can only text verified numbers.
        </div>
      </Section>

      <Section title="Alert on">
        <Row label="Entry (position opened)">
          <input type="checkbox" checked={cfg.events.entry} onChange={setField("events", "entry")} />
        </Row>
        <Row label="Exit (position closed)">
          <input type="checkbox" checked={cfg.events.exit} onChange={setField("events", "exit")} />
        </Row>
      </Section>

      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <button style={styles.button} onClick={save} disabled={busy}>{busy ? "Saving..." : "Save Alert Settings"}</button>
        <button style={styles.buttonSecondary} onClick={sendTest} disabled={testBusy}>{testBusy ? "Sending..." : "Send Test Alert"}</button>
      </div>

      {testResults && (
        <div style={{ marginTop: 12, fontSize: 12 }}>
          {Object.entries(testResults).map(([channel, result]) => (
            <div key={channel} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              <span style={{ ...styles.dot, background: result.ok ? "var(--accent)" : "var(--danger)" }} />
              <span style={{ textTransform: "capitalize", width: 70 }}>{channel}</span>
              <span style={{ color: "var(--text-dim)" }}>{result.ok ? "Sent successfully" : result.error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

const styles = {
  section: { background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 8, padding: 14, marginBottom: 14 },
  sectionTitle: { fontSize: 12.5, fontWeight: 600, marginBottom: 10 },
  row: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 },
  rowLabel: { width: 140, fontSize: 12, color: "var(--text-dim)", flexShrink: 0 },
  input: { width: "100%", background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 5, color: "var(--text)", padding: "6px 8px", fontSize: 12, boxSizing: "border-box" },
  hint: { fontSize: 10.5, color: "var(--text-dim)", marginTop: 4, lineHeight: 1.5 },
  button: { background: "var(--accent)", color: "#04140a", border: "none", borderRadius: 6, padding: "8px 16px", fontWeight: 600, fontSize: 12.5, cursor: "pointer" },
  buttonSecondary: { background: "var(--panel-alt)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 6, padding: "8px 16px", fontWeight: 600, fontSize: 12.5, cursor: "pointer" },
  banner: { padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 14 },
  bannerOk: { background: "rgba(46, 204, 113, 0.12)", color: "var(--accent)", border: "1px solid var(--accent)" },
  bannerErr: { background: "rgba(231, 76, 60, 0.12)", color: "var(--danger)", border: "1px solid var(--danger)" },
  dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
};
